import {
  AltiusRes,
  AuthenticateConnectionData,
  ConnectionPreview,
  ConnectionStatus,
  CreateConnectionData,
  DataItem,
  DataroomFilesFilters,
  DataRooms,
  DataroomTypesResponse,
  DateRange,
  DealDataromConnection,
  DeleteRelationReq,
  DownloadDataroomFilesType,
  FilterOptions,
  GetConnectionRes,
  IFolder,
  IK1TableData,
  Platform,
  ResendCodeData,
  SupplierData,
  UpdateConsumerRes,
  UpdateRelation,
  UpdateRelationReq
} from "store/types";
import downloadFileAxios from "utils/download-file-axios";

import { DataRoomsAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IDataRoomsActions {
  getConnectionsAction: () => Promise<ConnectionPreview[]>;
  getConnectionAction: (id: number) => Promise<void>;
  getPlatformsAction: () => Promise<Platform[]>;
  deleteConnection: (id: number) => Promise<void>;

  createConnectionAction: (data: CreateConnectionData) => Promise<number>;

  resendCodeAction: (data: ResendCodeData) => Promise<void>;
  clearConnectionAction: () => void;
  updateRelationAction: (data: UpdateRelationReq) => Promise<string | number>;
  deleteRelationAction: (data: DeleteRelationReq) => Promise<number | string>;

  authenticateConnectionAction: (data: AuthenticateConnectionData) => Promise<number>;

  downloadDataroomsFiles: (files: DataItem[], op: string) => Promise<void>;
  getDownloadedDataroomsFiles: (requestUuid: string) => Promise<void>;
  getTypeDataroomsFiles: (requestUuid: string, type: string) => Promise<void>;

  importFilesForDataroomAction: (connectionId: number) => Promise<string>;
  getConnectionsStatusAction: (dataroom: ConnectionPreview) => Promise<ConnectionPreview[]>;
  syncInvestorsToDeals: (id: number, matchWithDeals: boolean) => Promise<void>;
  resetFiles: (dataroomId: number, fetcherId: number) => Promise<string>;
  syncFetcherAction: (dataroomId: number, fetcherId: number) => Promise<string>;
  downloadStatusCsv: () => Promise<void>;

  downloadDealDataroomsFiles: (data: {
    op: DownloadDataroomFilesType;
    all_files: boolean;
    deal_id: number;
    folder_id: number;
    selected_files: number[];
    document_types?: string[];
    account_names?: string[];
    created_at?: DateRange | null;
    show_latest?: boolean;
  }) => Promise<void>;

  getDealDataroomsActions: (dealId: number) => Promise<Array<DealDataromConnection>>;
  setConnectMessageAction: (data: DataRooms["connectMessage"]) => void;
  setConnectMessageTimeoutAction: (timeout: string | null) => void;
  getFilesFiltersAction: (selectedFilters?: DataroomFilesFilters) => Promise<FilterOptions>;
  getConnectionsStatusesAction: () => Promise<ConnectionStatus[]>;
  getDealDataroomRoot: (data: { dealId: number }) => Promise<Record<number, IFolder>>;
  updateConnectionConfig: (id: number, config: { enable_auto_sync?: boolean }) => Promise<void>;
  getK1TableAction: () => Promise<IK1TableData[]>;
}

export class DataRoomsActions extends AsyncActions implements IDataRoomsActions {
  constructor(params: AsyncActionsParams) {
    super(params);
  }
  public getConnectionsAction = async (): Promise<ConnectionPreview[]> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ConnectionPreview[]>>({
      url: "/api/v0.0.3/datarooms"
    });
    this.dispatch({
      type: DataRoomsAction.GetConnectionsSucccess,
      payload: data
    });
    return data;
  };

  public getConnectionsStatusAction = async (dataroom: ConnectionPreview): Promise<ConnectionPreview[]> => {
    dataroom.is_login_valid = null;
    this.dispatch({
      type: DataRoomsAction.SaveConnectionPreview,
      payload: dataroom
    });
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ConnectionPreview[]>>({
      url: `/api/v0.0.3/dataroom-status/${dataroom.id}`
    });
    this.dispatch({
      type: DataRoomsAction.SaveConnectionPreview,
      payload: data
    });
    return data;
  };

  public getConnectionAction = async (id: number): Promise<void> => {
    const url =
      `/api/v0.0.3/datarooms/${id}` + (window.location.search.includes("reload_funds") ? "?reload_funds=true" : "");

    const [getConnectionRes, suppliers] = await Promise.all([
      this.api.get<AltiusRes<GetConnectionRes>>({ url }),
      this.api.get<AltiusRes<SupplierData[]>>({
        url: `/api/v0.0.3/datarooms/${id}/fetchers`
      })
    ]);

    this.dispatch({
      type: DataRoomsAction.GetConnectionSuccess,
      payload: {
        getConnectionRes: getConnectionRes.data.data,
        suppliers: suppliers.data.data
      }
    });
  };

  public getPlatformsAction = async (): Promise<Platform[]> => {
    const res = await this.api.get<AltiusRes<DataroomTypesResponse>>({
      url: "/api/v0.0.3/dataroom-types"
    });
    const platforms = Object.entries(res.data.data).map(([id, dataroomType]) => ({
      id: id,
      name: dataroomType.name,
      logo: dataroomType.logo,
      extra: dataroomType.extra
    }));
    this.dispatch({
      type: DataRoomsAction.SetPlatforms,
      payload: platforms
    });
    return platforms;
  };

  public deleteConnection = async (id: number): Promise<void> => {
    await this.api.delete({ url: `/api/v0.0.3/datarooms/${id}` });
    this.dispatch({
      type: DataRoomsAction.DeleteConnectionSuccess,
      payload: id
    });
  };

  public createConnectionAction = async (data: CreateConnectionData): Promise<number> => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ConnectionPreview>>({
      url: "/api/v0.0.3/datarooms",
      data
    });
    return payload.id;
  };

  public authenticateConnectionAction = async ({
    connection_id,
    ...body
  }: AuthenticateConnectionData): Promise<number> => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<ConnectionPreview>>({
      url: `/api/v0.0.3/datarooms/${connection_id}/authenticate`,
      data: body
    });
    this.dispatch({ type: DataRoomsAction.SetConnectMessage, payload: data });
    return data.id;
  };

  public resendCodeAction = async (data: ResendCodeData): Promise<void> => {
    await this.api.post({
      url: `/api/v0.0.3/datarooms/${data.connection_id}/authenticate`
    });
  };

  public clearConnectionAction = (): void => {
    this.dispatch({
      type: DataRoomsAction.GetConnectionSuccess,
      payload: null
    });
  };

  public updateRelationAction = async (data: UpdateRelationReq): Promise<string | number> => {
    const { dataroomId, consumerId, supplierId, new_deal } = data;
    const {
      data: {
        data: { dataroom, id, fund }
      }
    } = await this.api.post<AltiusRes<UpdateConsumerRes>>({
      url: `/api/v0.0.3/datarooms/${dataroomId}/fetchers`,
      data: {
        deal: consumerId,
        dataroom_fund: supplierId,
        new_deal
      }
    });

    const dataToUpdate: UpdateRelation = {
      dataroomId: dataroom,
      supplierId: supplierId,
      consumer: {
        id,
        dealId: fund
      }
    };

    this.dispatch({
      type: DataRoomsAction.UpdateRelationSuccess,
      payload: dataToUpdate
    });
    return data.supplierId;
  };

  public deleteRelationAction = async (data: DeleteRelationReq): Promise<number | string> => {
    const { dataroomId, consumerId, supplierId } = data;
    await this.api.delete({
      url: `/api/v0.0.3/datarooms/${dataroomId}/fetchers/${consumerId}`
    });

    const dataToUpdate: UpdateRelation = {
      dataroomId,
      supplierId,
      consumer: null
    };

    this.dispatch({
      type: DataRoomsAction.UpdateRelationSuccess,
      payload: dataToUpdate
    });
    return data.supplierId;
  };

  public downloadDataroomsFiles = async (files: DataItem[], op: string): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.3/datarooms/download-selected-dataroom-files",
      data: { files, op },
      responseType: "json"
    });
    // .then(downloadFileAxios);
  };

  public getDownloadedDataroomsFiles = async (requestUuid: string): Promise<void> => {
    return this.api
      .post({
        url: "/api/v0.0.3/datarooms/get-selected-dataroom-files/" + requestUuid,
        // data: { requestUuid },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };
  public getTypeDataroomsFiles = async (requestUuid: string, type: string): Promise<void> => {
    return this.api
      .post({
        url: "/api/v0.0.3/datarooms/get-selected-dataroom-files/" + type + "/" + requestUuid,
        // data: { requestUuid },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public importFilesForDataroomAction = async (connectionId: number): Promise<string> => {
    const {
      data: { message }
    } = await this.api.post<AltiusRes<[]>>({
      url: `/api/v0.0.3/datarooms/${connectionId}/sync`
    });
    return message;
  };

  public syncInvestorsToDeals = async (id: number, matchWithDeals: boolean): Promise<void> => {
    await this.api.get({
      url: `/api/v0.0.3/datarooms/${id}/sync-investors`,
      params: { matchWithDeals }
    });
  };

  public resetFiles = async (dataroomId: number, fetcherId: number): Promise<string> => {
    const res = await this.api.put<AltiusRes<string>>({
      url: `/api/v0.0.3/datarooms/${dataroomId}/fetchers/${fetcherId}/resetfiles`
    });
    return res?.data?.message;
  };

  public syncFetcherAction = async (dataroomId: number, fetcherId: number): Promise<string> => {
    const res = await this.api.post<AltiusRes<string>>({
      url: `/api/v0.0.3/datarooms/${dataroomId}/fetchers/${fetcherId}/sync`
    });
    return res?.data?.message;
  };

  public downloadStatusCsv = async (): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/datarooms/status-csv",
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public downloadDealDataroomsFiles: IDataRoomsActions["downloadDealDataroomsFiles"] = async ({
    all_files,
    selected_files,
    created_at,
    ...rest
  }) => {
    await this.api.get({
      url: "/api/v0.0.3/",
      params: {
        ...(all_files ? { all_files } : { selected_files }),
        ...created_at,
        ...rest
      }
    });
  };

  public getDealDataroomsActions: IDataRoomsActions["getDealDataroomsActions"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DealDataromConnection[]>>({
      url: `/api/v0.0.3/deals/${dealId}/datarooms`
    });
    return data;
  };

  public setConnectMessageAction: IDataRoomsActions["setConnectMessageAction"] = async (payload) => {
    this.dispatch({ type: DataRoomsAction.SetConnectMessage, payload });
  };

  public setConnectMessageTimeoutAction: IDataRoomsActions["setConnectMessageTimeoutAction"] = async (payload) => {
    this.dispatch({
      type: DataRoomsAction.SetConnectMessageTimeout,
      payload
    });
  };

  public getFilesFiltersAction: IDataRoomsActions["getFilesFiltersAction"] = async (selectedFilters) => {
    // Temporary use Array<{ name: string }> instead of simple string[], later it should be replaced with ids[] - numbers;
    const updatedArrayFilters: Partial<
      Record<keyof Pick<DataroomFilesFilters, "accounts" | "altiusDeals" | "docTypes" | "deals">, { name: string }[]>
    > = {};

    for (const key in selectedFilters) {
      const values = selectedFilters[key as keyof DataroomFilesFilters];
      if (key.includes("dataroomsIds")) continue;
      if (Array.isArray(values)) {
        updatedArrayFilters[
          key as keyof Pick<DataroomFilesFilters, "accounts" | "altiusDeals" | "docTypes" | "deals">
        ] = values.map((item) => ({ name: item.toString() }));
      }
    }

    const {
      data: { data }
    } = await this.api.get<AltiusRes<FilterOptions>>({
      url: "/api/v0.0.3/datarooms/files/filters",
      ...(selectedFilters && {
        params: { filters: { ...selectedFilters, ...updatedArrayFilters } }
      })
    });
    return data;
  };

  public getConnectionsStatusesAction: IDataRoomsActions["getConnectionsStatusesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ConnectionStatus[]>>({
      url: "/api/v0.0.3/datarooms/status"
    });
    return data;
  };

  public getDealDataroomRoot: IDataRoomsActions["getDealDataroomRoot"] = async ({ dealId }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFolder>>>({
      url: `/api/v0.0.3/deals/${dealId}/folders`,
      ...{ params: { kind: "dataroom_root" } }
    });
    return payload;
  };

  public getK1TableAction: IDataRoomsActions["getK1TableAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IK1TableData[]>>({
      url: "/api/v0.0.3/datarooms-k1"
    });
    return data;
  };

  public updateConnectionConfig: IDataRoomsActions["updateConnectionConfig"] = async (id, config) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<ConnectionPreview>>({
      url: `/api/v0.0.3/datarooms/${id}/config`,
      data: config
    });
    this.dispatch({
      type: DataRoomsAction.SaveConnectionPreview,
      payload: data
    });
  };
}
